import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DataTable, DateDisplay, IDataTableColumn, Section, ShowMoreButton } from '@lib/common';
import { GuestSurvey, UpdateGuestSurveyOutreachRequest } from '@lib/state';
import { Grid, Tooltip, Typography } from '@material-ui/core';
import React, { useCallback, useState } from 'react';
import { SurveyUpdateModal } from './survey-update.modal';

interface Props {
  surveys: Array<GuestSurvey>;
  loading: boolean;
  onEditSubmit: (guestSurvey: UpdateGuestSurveyOutreachRequest) => void;
  isDone: boolean;
  onShowMore: () => void;
}

export function SurveyTable({ surveys, loading, onEditSubmit, isDone, onShowMore }: Props) {
  const [surveyModalOpen, setSurveyModalOpen] = useState(false);
  const [activeSurvey, setActiveSurvey] = useState<GuestSurvey | undefined>(undefined);

  const handleSurveyClick = useCallback((survey: GuestSurvey) => {
    setActiveSurvey(survey);
    setSurveyModalOpen(true);
  }, []);

  const onModalClose = useCallback(() => {
    setSurveyModalOpen(false);
    setActiveSurvey(undefined);
  }, []);

  const columns = getColumns(handleSurveyClick);

  return (
    <>
      {activeSurvey && (
        <SurveyUpdateModal
          open={surveyModalOpen}
          guestSurvey={activeSurvey}
          onClose={onModalClose}
          onSubmit={onEditSubmit}
        />
      )}
      <Section title="Results" textAlign="left" disableGutters>
        <DataTable
          loading={loading}
          items={surveys}
          columns={columns}
          getItemKey={s => s.id ?? ''}
          children={<ShowMoreButton isDone={isDone} loadMore={onShowMore} />}
        />
      </Section>
    </>
  );
}

const getColumns: (
  surveyClicked: (survey: GuestSurvey) => void
) => Array<IDataTableColumn<GuestSurvey>> = surveyClicked => [
  {
    title: '',
    valueFactory: s => (
      <Tooltip title={`Outreach ${s.outreachCompleted ? 'Completed' : 'Incomplete'}`}>
        <Grid>
          <FontAwesomeIcon
            icon={s.outreachCompleted ? 'check-circle' : 'times-circle'}
            style={{ fontSize: '1.5em' }}
            color={s.outreachCompleted ? 'green' : 'red'}
          />
        </Grid>
      </Tooltip>
    ),
    colSpan: 0.25,
  },
  {
    title: 'Reservation Number',
    valueFactory: s => (
      <Typography
        component="button"
        type="button"
        style={{
          textDecoration: 'underline',
          cursor: 'pointer',
          background: 'none',
          border: 'none',
          padding: 0,
          font: 'inherit',
        }}
        onClick={() => surveyClicked(s)}
      >
        {s.recordNumber}
      </Typography>
    ),
    colSpan: 0.5,
  },
  {
    title: 'Phone Number',
    valueFactory: s => s.guestPhone,
    colSpan: 0.5,
  },
  {
    title: 'Email',
    valueFactory: s => s.guestEmail,
  },
  {
    title: 'Survey Date',
    valueFactory: s => (
      <DateDisplay date={!!s.completedTime ? new Date(s.completedTime) : undefined} />
    ),
    colSpan: 0.5,
  },
  {
    title: 'Guest Name',
    valueFactory: s => s.guestName,
  },
  {
    title: 'Average Rating',
    valueFactory: s => {
      const ratings = [s.foodRating, s.staffRating, s.facilitiesRating].filter(
        r => r != null
      ) as number[];
      return ratings.length > 0
        ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(2)
        : '-';
    },
    colSpan: 0.5,
  },
  {
    title: 'Food Rating',
    valueFactory: s => s.foodRating ?? '-',
    colSpan: 0.5,
  },
  {
    title: 'Staff Rating',
    valueFactory: s => s.staffRating ?? '-',
    colSpan: 0.5,
  },
  {
    title: 'Facilities Rating',
    valueFactory: s => s.facilitiesRating ?? '-',
    colSpan: 0.5,
  },
  {
    title: 'Comments',
    valueFactory: s => <Grid style={{ maxHeight: '4em', overflowY: 'auto' }}>{s.comments}</Grid>,
  },
  {
    title: 'Outreach Notes',
    valueFactory: s => (
      <Grid component="ul" container style={{ maxHeight: '4em', overflowY: 'auto' }}>
        {s.outreachNotes?.map((n, i) => (
          <Grid component="li" item xs={12} key={i}>
            {n}
          </Grid>
        ))}
      </Grid>
    ),
  },
];
